// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import {
  DASHBOARD_STALE_TIME,
  getDashboardStats,
} from "@libraryadmin/services/api";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { StatCard } from "@libraryadmin/components/dashboard/StatCard";
import { QuickActions } from "@libraryadmin/components/dashboard/QuickActions";
import { RecentActivity } from "@libraryadmin/components/dashboard/RecentActivity";
import {
  IssueReceiveChart,
  CollectionPieChart,
} from "@libraryadmin/components/dashboard/Charts";
import {
  BookOpen,
  ScrollText,
  Users,
  AlertCircle,
  Sparkles,
  Library,
  BookMarked,
} from "lucide-react";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";

const StatCardSkeleton = () => (
  <div className="stat-card">
    <Skeleton className="mb-3 h-4 w-28" />
    <Skeleton className="mb-2 h-9 w-20" />
    <Skeleton className="h-3 w-36" />
  </div>
);

const Index = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: getDashboardStats,
  });

  return (
    <DashboardLayout
      title="Dashboard"
      subtitle="Welcome back! Here's what's happening at Jambushrushti Jain Library."
    >
      {/* Welcome Banner */}
      <div className="animate-fade-in group relative mb-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-primary via-primary/95 to-secondary p-5 text-white shadow-[0_20px_50px_rgba(210,129,29,0.15)] sm:mb-8 sm:rounded-3xl sm:p-8 md:p-10">
        {/* Lotus background motif */}
        <div className="lotus-pattern absolute inset-0 opacity-10 mix-blend-overlay" />

        {/* Glowing visual overlay blobs */}
        <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-accent/25 blur-[80px] transition-all duration-700 group-hover:bg-accent/30" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-60 w-60 rounded-full bg-secondary/35 blur-[60px]" />

        <div className="relative z-10">
          <div className="mb-3 flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 backdrop-blur-md">
            <Sparkles className="w-4.5 h-4.5 animate-pulse-soft text-accent" />
            <span className="text-xs font-semibold uppercase tracking-wider text-white/95">
              Jain Heritage Collection
            </span>
          </div>
          <h2 className="mb-3 bg-clip-text text-2xl font-extrabold tracking-tight text-white sm:text-3xl md:text-4xl">
            Preserving Ancient Wisdom
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
            Managing sacred texts, Agamas, and Jain manuscripts for generations
            to come with modern stewardship and reverence.
          </p>
          <div className="mt-6 flex flex-wrap gap-3.5">
            <div className="px-4.5 flex cursor-default items-center gap-2.5 rounded-2xl border border-white/15 bg-white/10 py-2.5 text-sm font-semibold shadow-sm backdrop-blur-md transition-all duration-300 hover:bg-white/15">
              <BookMarked className="h-4 w-4 text-accent" />
              <span>
                {stats
                  ? `${stats.totalManuscripts.toLocaleString()} Manuscripts`
                  : "Loading..."}
              </span>
            </div>
            <div className="px-4.5 flex cursor-default items-center gap-2.5 rounded-2xl border border-white/15 bg-white/10 py-2.5 text-sm font-semibold shadow-sm backdrop-blur-md transition-all duration-300 hover:bg-white/15">
              <Library className="h-4 w-4 text-accent" />
              <span>
                {stats
                  ? `${stats.totalBooks.toLocaleString()} Books`
                  : "Loading..."}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Books"
              value={stats?.totalBooks.toLocaleString() ?? "—"}
              icon={BookOpen}
              variant="primary"
              delay={0}
            />
            <StatCard
              title="Jain Manuscripts"
              value={stats?.totalManuscripts.toLocaleString() ?? "—"}
              icon={ScrollText}
              variant="primary"
              delay={1}
            />
            <StatCard
              title="Active Members"
              value={stats?.activeMembers.toLocaleString() ?? "—"}
              icon={Users}
              variant="warning"
              trend={{
                value: stats?.newMembersThisMonth ?? 0,
                label: "new this month",
              }}
              delay={2}
            />
            <StatCard
              title="Overdue Books"
              value={stats?.overdueBooks ?? "—"}
              icon={AlertCircle}
              variant="destructive"
              delay={3}
            />
          </>
        )}
      </div>

      {/* Charts Row */}
      <div className="mb-6 grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2">
        <IssueReceiveChart />
        <CollectionPieChart />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2">
        <QuickActions />
        <RecentActivity />
      </div>
    </DashboardLayout>
  );
};

export default Index;
